"use client";

import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import {
  Attachment,
  AttachmentPreview,
  Attachments,
} from "@/components/ai-elements/attachments";
import { Message, MessageContent, MessageResponse } from "@/components/ai-elements/message";
import {
  PromptInput,
  PromptInputActionAddAttachments,
  PromptInputActionAddScreenshot,
  PromptInputActionMenu,
  PromptInputActionMenuContent,
  PromptInputActionMenuTrigger,
  PromptInputAttachmentPreviews,
  PromptInputBody,
  PromptInputFooter,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
  type PromptInputMessage,
} from "@/components/ai-elements/prompt-input";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { Suggestion, Suggestions } from "@/components/ai-elements/suggestion";
import { Button } from "@/components/ui/button";
import { findHelpDoc } from "@/lib/docs/help";
import {
  HELP_CHAT_ATTACHMENT_TYPES,
  HELP_CHAT_MAX_ATTACHMENTS,
  withRecentAttachmentsOnly,
} from "@/lib/docs/help-chat-messages";
import { downscaleImageDataUrl } from "@/lib/image-downscale";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type FileUIPart, type UIMessage } from "ai";
import { BookOpenTextIcon, ExternalLinkIcon, PlusIcon } from "lucide-react";
import { useMemo } from "react";
import { toast } from "sonner";

// ── Config ─────────────────────────────────────────────────────────────────

const HELP_SUGGESTIONS = [
  "What's new?",
  "Where do I start? Please help me get started.",
  "Give me a crash course on Woods AI",
  "Make me an AI expert",
  "What does the Woods AI policy say about client information?",
] as const;

const ACCEPTED_ATTACHMENTS = HELP_CHAT_ATTACHMENT_TYPES.join(",");
// Checked before downscaling; images are shrunk to ~1568px before sending.
const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

const TOOL_ACTIVITY: Record<string, string> = {
  get_woods_help: "Reading the documentation…",
  get_woods_ai_policy: "Reading the Woods AI policy…",
  web_search: "Searching the web…",
};

type ToolPartLike = {
  type: string;
  state?: string;
  input?: { doc_id?: unknown };
  output?: unknown;
};

type SourceUrlPart = { type: "source-url"; url: string; title?: string };

// ── Helpers ────────────────────────────────────────────────────────────────

/** Docs pages the answer was built from, in the order they were read. */
function docsReadIn(message: UIMessage) {
  const ids = new Set<string>();
  for (const part of message.parts as ToolPartLike[]) {
    if (part.type !== "tool-get_woods_help" || part.state !== "output-available") continue;
    const docId = part.input?.doc_id;
    if (typeof docId === "string" && findHelpDoc(docId)) ids.add(docId);
  }
  return [...ids].map((id) => findHelpDoc(id)!);
}

function webSourcesIn(message: UIMessage) {
  const seen = new Map<string, SourceUrlPart>();
  for (const part of message.parts) {
    if (part.type === "source-url" && !seen.has(part.url)) seen.set(part.url, part as SourceUrlPart);
  }
  return [...seen.values()].slice(0, 6);
}

/** Follow-up questions the route suggests once an answer finishes. */
function followUpsIn(message: UIMessage): string[] {
  for (const part of message.parts) {
    if (part.type !== "data-follow-ups") continue;
    const questions = (part.data as { questions?: unknown } | undefined)?.questions;
    if (Array.isArray(questions)) return questions.filter((q): q is string => typeof q === "string").slice(0, 3);
  }
  return [];
}

const hostOf = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

// ── Component ──────────────────────────────────────────────────────────────

type DocsHelpChatProps = {
  onOpenDoc: (docId: string) => void;
};

export function DocsHelpChat({ onOpenDoc }: DocsHelpChatProps) {
  // Stateless by design: the conversation lives only in this component and
  // the route persists nothing.
  const transport = useMemo(
    () =>
      new DefaultChatTransport<UIMessage>({
        api: "/api/docs/help-chat",
        prepareSendMessagesRequest: async ({ messages, body }) => {
          const { data } = await supabase.auth.getSession();
          const token = data.session?.access_token;
          return {
            body: { ...body, messages: withRecentAttachmentsOnly(messages) },
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          };
        },
      }),
    []
  );
  const { messages, sendMessage, setMessages, status, stop, error } = useChat<UIMessage>({ transport });

  const isBusy = status === "submitted" || status === "streaming";
  const hasMessages = messages.length > 0;

  const handleSend = (text: string, files: FileUIPart[] = []) => {
    const trimmed = text.trim();
    if ((!trimmed && files.length === 0) || isBusy) return;
    void sendMessage({ text: trimmed || "Here's a screenshot.", files });
  };

  const handleSubmit = async (message: PromptInputMessage) => {
    const files = await Promise.all(
      message.files.map(async (file) => {
        if (!file.url.startsWith("data:image/")) return file;
        const { url, mediaType } = await downscaleImageDataUrl(file.url);
        return { ...file, url, mediaType };
      })
    );
    handleSend(message.text, files);
  };

  const handleAttachmentError = ({ code, message }: { code: string; message: string }) => {
    toast.error(
      code === "accept"
        ? "Only images and screenshots can be added here."
        : code === "max_file_size"
          ? "That image is too large."
          : code === "max_files"
            ? `You can add up to ${HELP_CHAT_MAX_ATTACHMENTS} images at a time.`
            : message
    );
  };

  const handleNewChat = () => {
    if (isBusy) void stop();
    setMessages([]);
  };

  const composer = (
    <PromptInput
      accept={ACCEPTED_ATTACHMENTS}
      maxFileSize={MAX_ATTACHMENT_BYTES}
      maxFiles={HELP_CHAT_MAX_ATTACHMENTS}
      multiple
      onError={handleAttachmentError}
      onSubmit={handleSubmit}
    >
      <PromptInputBody>
        <PromptInputAttachmentPreviews />
        <PromptInputTextarea placeholder="Ask anything about Woods AI, or paste a screenshot…" />
      </PromptInputBody>
      <PromptInputFooter>
        <PromptInputTools>
          <PromptInputActionMenu>
            <PromptInputActionMenuTrigger tooltip={{ content: "Add image or screenshot", side: "top" }} />
            <PromptInputActionMenuContent>
              <PromptInputActionAddAttachments label="Add images" />
              <PromptInputActionAddScreenshot />
            </PromptInputActionMenuContent>
          </PromptInputActionMenu>
          {hasMessages ? (
            <Button
              className="gap-1.5 text-muted-foreground"
              disabled={isBusy}
              onClick={handleNewChat}
              size="sm"
              type="button"
              variant="ghost"
            >
              <PlusIcon className="size-3.5" />
              New chat
            </Button>
          ) : (
            <span className="px-2 text-xs text-muted-foreground">Not saved</span>
          )}
        </PromptInputTools>
        <PromptInputSubmit onStop={() => void stop()} status={status} />
      </PromptInputFooter>
    </PromptInput>
  );

  if (!hasMessages) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col justify-center gap-6 px-4 py-10 sm:px-6">
          <div className="space-y-2 text-center">
            <h2 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
              How can I help with Woods AI?
            </h2>
            <p className="text-sm text-muted-foreground">
              Answers come from the Woods AI guides and policy. These chats aren&apos;t saved.
            </p>
          </div>
          {composer}
          <Suggestions className="w-full flex-wrap justify-center">
            {HELP_SUGGESTIONS.map((suggestion) => (
              <Suggestion key={suggestion} onClick={handleSend} suggestion={suggestion} />
            ))}
          </Suggestions>
        </div>
      </div>
    );
  }

  const lastMessage = messages[messages.length - 1];

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <Conversation>
        <ConversationContent className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
          {messages.map((message) => {
            const isLast = message === lastMessage;
            const docs = message.role === "assistant" ? docsReadIn(message) : [];
            const webSources = message.role === "assistant" ? webSourcesIn(message) : [];
            // One activity line for the tool currently running, if any.
            const runningTool = isLast && isBusy
              ? (message.parts as ToolPartLike[]).findLast(
                  (part) => part.type.startsWith("tool-") && part.state !== "output-available" && part.state !== "output-error"
                )
              : undefined;
            const hasText = message.parts.some((part) => part.type === "text" && part.text.trim());
            const fileParts = message.parts.filter((part): part is FileUIPart => part.type === "file");
            // Only the latest answer offers follow-ups; older ones would be stale.
            const followUps = isLast && !isBusy && message.role === "assistant" ? followUpsIn(message) : [];

            return (
              <Message from={message.role} key={message.id}>
                {fileParts.length > 0 ? (
                  <Attachments className="ml-auto justify-end" variant="grid">
                    {fileParts.map((part, index) => (
                      <Attachment data={{ ...part, id: `${message.id}-${index}` }} key={index}>
                        <AttachmentPreview />
                      </Attachment>
                    ))}
                  </Attachments>
                ) : null}
                <MessageContent>
                  {message.parts.map((part, index) =>
                    part.type === "text" ? (
                      message.role === "user" ? (
                        <p className="whitespace-pre-wrap" key={index}>{part.text}</p>
                      ) : (
                        <MessageResponse
                          disallowedElements={["script", "style"]}
                          isAnimating={isLast && status === "streaming"}
                          key={index}
                        >
                          {part.text}
                        </MessageResponse>
                      )
                    ) : null
                  )}
                  {runningTool ? (
                    <Shimmer as="span" className="text-sm" duration={1.6}>
                      {TOOL_ACTIVITY[runningTool.type.replace(/^tool-/, "")] ?? "Working…"}
                    </Shimmer>
                  ) : isLast && isBusy && message.role === "assistant" && !hasText ? (
                    <Shimmer as="span" className="text-sm" duration={1.6}>
                      Thinking…
                    </Shimmer>
                  ) : null}
                </MessageContent>
                {docs.length > 0 && !(isLast && isBusy) ? (
                  <div className="mx-auto flex w-[95%] flex-wrap items-center gap-2">
                    <span className="text-xs text-muted-foreground">From the docs:</span>
                    {docs.map((doc) => (
                      <Suggestion key={doc.id} onClick={onOpenDoc} suggestion={doc.id}>
                        <BookOpenTextIcon className="size-3.5" />
                        {doc.title}
                      </Suggestion>
                    ))}
                  </div>
                ) : null}
                {webSources.length > 0 && !(isLast && isBusy) ? (
                  <div className="mx-auto flex w-[95%] flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="text-xs text-muted-foreground">Web sources:</span>
                    {webSources.map((source) => (
                      <a
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                        href={source.url}
                        key={source.url}
                        rel="noreferrer"
                        target="_blank"
                      >
                        {source.title || hostOf(source.url)}
                        <ExternalLinkIcon className="size-3" />
                      </a>
                    ))}
                  </div>
                ) : null}
                {followUps.length > 0 ? (
                  <Suggestions className="mx-auto w-[95%] flex-wrap pt-1">
                    {followUps.map((question) => (
                      <Suggestion
                        className="h-auto whitespace-normal py-1.5 text-left"
                        key={question}
                        onClick={handleSend}
                        suggestion={question}
                      />
                    ))}
                  </Suggestions>
                ) : null}
              </Message>
            );
          })}
          {status === "submitted" && lastMessage?.role === "user" ? (
            <Message from="assistant">
              <MessageContent>
                <Shimmer as="span" className="text-sm" duration={1.6}>
                  Thinking…
                </Shimmer>
              </MessageContent>
            </Message>
          ) : null}
          {error ? (
            <p className={cn("mx-auto w-[95%] text-sm text-destructive")}>
              Something went wrong. Please try again.
            </p>
          ) : null}
        </ConversationContent>
        <ConversationScrollButton />
      </Conversation>
      <div className="mx-auto w-full max-w-3xl shrink-0 px-4 pb-4 sm:px-6">{composer}</div>
    </div>
  );
}
